import React, { useState, useEffect } from 'react';
import { formatPrice } from '../utils/formatPrice';
import './EmailReportPopup.css';

interface EmailReportPopupProps {
    isOpen: boolean;
    onClose: () => void;
    onSend: (to: string, subject: string, message: string) => Promise<void>;
    shiftId: string;
    cashierName: string;
    netSales: number;
    defaultEmail?: string;
}

const EmailReportPopup: React.FC<EmailReportPopupProps> = ({
    isOpen,
    onClose,
    onSend,
    shiftId,
    cashierName,
    netSales,
    defaultEmail = '',
}) => {
    const [to, setTo] = useState(defaultEmail);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState('');

    const formatNumber = (value: number) => {
        return formatPrice(value);
    };

    useEffect(() => {
        if (isOpen) {
            const today = new Date().toISOString().split('T')[0];
            setSubject(`Z-Report #${shiftId} - ${today}`);
            setMessage(
                `Please find attached the Z-Report for shift #${shiftId}.\n\nCashier: ${cashierName}\nNet Sales: ${formatNumber(netSales)}`
            );
            setError('');
        }
    }, [isOpen, shiftId, cashierName, netSales]);

    const handleSend = async () => {
        if (!to.trim()) {
            setError('Please enter an email address');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(to)) {
            setError('Please enter a valid email address');
            return;
        }

        setIsSending(true);
        setError('');

        try {
            await onSend(to, subject, message);
            onClose();
        } catch (err) {
            setError('Failed to send email. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="email-popup-overlay" onClick={onClose}>
            <div className="email-popup ds-pop" onClick={(e) => e.stopPropagation()}>
                <div className="email-popup-header">
                    <div className="email-popup-title">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M22 6L12 13L2 6" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Email Report</span>
                    </div>
                    <button className="email-popup-close" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>

                <div className="email-popup-content">
                    <div className="email-field">
                        <label>TO</label>
                        <input
                            type="email"
                            value={to}
                            onChange={(e) => setTo(e.target.value)}
                            placeholder="Enter email address"
                        />
                    </div>

                    <div className="email-field">
                        <label>SUBJECT</label>
                        <input
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </div>

                    <div className="email-field">
                        <label>MESSAGE</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={4}
                        />
                    </div>

                    <div className="email-attachment">
                        <div className="attachment-icon">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M14 2V8H20" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <div className="attachment-info">
                            <span className="attachment-name">Z-Report-{shiftId}.pdf</span>
                            <span className="attachment-meta">~145 KB • Generated just now</span>
                        </div>
                    </div>

                    {error && <div className="email-error">{error}</div>}
                </div>

                <div className="email-popup-actions">
                    <button className="email-cancel-btn" onClick={onClose} disabled={isSending}>
                        Cancel
                    </button>
                    <button className="email-send-btn" onClick={handleSend} disabled={isSending}>
                        {isSending ? (
                            'Sending...'
                        ) : (
                            <>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Send Email
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailReportPopup;